import type { ApolloClient, RefetchQueriesInclude } from '@apollo/client';
import {
  GetFolderAssets,
  type GetFolderAssetsQuery,
  type GetFolderAssetsQueryVariables,
} from '@frameio/assets/shared/gql/generated/GetFolderAssets';
import { getFolderAssetsConnection } from '@frameio/assets/shared/utils/getFolderAssetsConnection';
import type { FinderRefetchOptions } from '@frameio/shared/contexts/FinderRefetchContext';
import { getIsOrderPossiblyChanged } from './getIsOrderPossiblyChanged';
import type { GetFolderAssetsObservableQuery } from './shared';

type AssetType = GetFolderAssetsQueryVariables['assetType'];

/**
 * getRefetchFolderAssetsOptions
 *
 * This utility function returns refetch query options for Finder 2 assets.
 *
 * @param {ApolloClient} client  Apollo client
 * @param {FinderRefetchOptions} opts Refetch options
 * @returns {RefetchQueriesOptions}
 */
export const getRefetchFolderAssetsOptions = (
  client: ApolloClient<any>,
  observedQueryNames: Set<string>,
  activeFolderId: string,
  opts?: FinderRefetchOptions
) => {
  const include: RefetchQueriesInclude = [];
  observedQueryNames.forEach((queryName) => {
    if (queryName === 'GetFolderAssets') {
      include.push(GetFolderAssets);
    }
  });
  return {
    include,
    onQueryUpdated: async (query: GetFolderAssetsObservableQuery) => {
      const variables = query.variables;
      if (
        opts?.changedFieldDefinitionIds &&
        !getIsOrderPossiblyChanged(query, opts.changedFieldDefinitionIds)
      ) {
        return false;
      }

      if (
        (activeFolderId && variables?.folderId !== activeFolderId) ||
        !variables?.sortBy
      ) {
        return false;
      }

      const folderAssets = getFolderAssetsConnection(
        query.getCurrentResult().data
      );
      const fetchedNodes = folderAssets?.nodes ?? [];

      const refetchedNodes: NonNullable<typeof folderAssets>['nodes'] = [];

      const shouldContinueFetching = (
        assetType: AssetType,
        hasNextPage?: boolean
      ) => {
        if (assetType === 'FOLDER' && hasNextPage === false) {
          return true;
        }

        //if there aren't groups we need to fetch until we have at least the number of nodes we started with
        return hasNextPage && refetchedNodes.length < fetchedNodes?.length;
      };

      const fetchNextPage = async (
        assetType: AssetType = 'FOLDER',
        after: string | null = null
      ) => {
        return client.query<
          GetFolderAssetsQuery,
          GetFolderAssetsQueryVariables
        >({
          query: query.query,
          fetchPolicy: 'network-only',
          variables: {
            ...variables,
            assetType,
            page: {
              first: 1000,
              after,
            },
          },
        });
      };

      const fetchSequentially = async (
        assetType: AssetType = 'FOLDER',
        after: string | null = null
      ) => {
        const { data } = await fetchNextPage(assetType, after);
        const folderAssets = getFolderAssetsConnection(data);

        if (!folderAssets?.nodes) return;

        refetchedNodes.push(...folderAssets.nodes);

        const pageInfo = folderAssets.pageInfo;
        const switchToFiles =
          assetType === 'FOLDER' && pageInfo.hasNextPage === false;
        const nextAssetType: AssetType = switchToFiles ? 'FILE' : 'FOLDER';
        const nextCursor = switchToFiles ? null : pageInfo.endCursor;

        if (shouldContinueFetching(assetType, pageInfo?.hasNextPage ?? false)) {
          await fetchSequentially(nextAssetType, nextCursor);
        }
      };

      fetchSequentially();
      return false;
    },
  };
};
